import type { Knex } from 'knex'
import { nexusEvents } from '../../core/index.js'
import { logger } from '@kernel/logger/index.js'
import {
  extractTableFromInsert,
  extractTableFromUpdate,
  extractTableFromDelete,
  extractTableFromSelect
} from './sql-utils.js'

// Tablas a ignorar (internas del sistema)
const IGNORED_TABLES = new Set(['refresh_tokens', 'knex_migrations', 'knex_migrations_lock'])

/** Default slow query threshold in ms */
const DEFAULT_SLOW_QUERY_MS = 500

export interface DbEventPayload {
  table: string
  action: 'created' | 'updated' | 'deleted'
  data: unknown
  timestamp: Date
}

export function setupQueryInterceptor(knexInstance: Knex): Knex {
  const queryTimings = new Map<string, number>()
  const slowQueryMs = Number(process.env['NEXUS_SLOW_QUERY_MS']) || DEFAULT_SLOW_QUERY_MS

  // Track query start time
  knexInstance.on('query', (query) => {
    if (query.__knexQueryUid) {
      queryTimings.set(query.__knexQueryUid, Date.now())
    }
  })

  // Interceptar eventos de query para emitir eventos CRUD + slow query detection
  knexInstance.on('query-response', (response, query) => {
    const sql = query.sql?.toLowerCase() ?? ''

    // Slow query detection
    const startTime = queryTimings.get(query.__knexQueryUid)
    if (startTime) {
      queryTimings.delete(query.__knexQueryUid)
      const duration = Date.now() - startTime
      if (duration >= slowQueryMs) {
        const table = extractTableFromSelect(sql)
          ?? extractTableFromInsert(sql)
          ?? extractTableFromUpdate(sql)
          ?? extractTableFromDelete(sql)
        logger.warn({ sql: query.sql?.substring(0, 200), duration, table }, 'Slow query detected')
      }
    }

    // Detectar tipo de operación desde SQL
    let action: string | undefined
    let table: string | undefined

    if (sql.startsWith('insert into')) {
      action = 'created'
      table = extractTableFromInsert(sql)
    } else if (sql.startsWith('update')) {
      action = 'updated'
      table = extractTableFromUpdate(sql)
    } else if (sql.startsWith('delete from')) {
      action = 'deleted'
      table = extractTableFromDelete(sql)
    }

    // Emitir evento si es operación CRUD válida
    if (action && table && !IGNORED_TABLES.has(table)) {
      nexusEvents.emit(`db.${table}.${action}`, {
        table,
        action,
        data: response,
        timestamp: new Date()
      } as DbEventPayload)
    }
  })

  // Cleanup on query error to prevent memory leaks
  knexInstance.on('query-error', (_error, query) => {
    queryTimings.delete(query.__knexQueryUid)
  })

  return knexInstance
}
